import { ALL, LIVE_AREAS, type MovedAreas } from "./useLiveUpdates.areas";

// Release 1.5.0 — what this tab last knew about each data area's version. Both the push connection and the 30-second
// check report here, so a change seen by one is never applied twice by the other, and a reconnect can tell exactly what
// was missed while the connection was down.

let nonce: string | null = null;
let known: Map<string, number> | null = null;

const PUSHABLE = new Set<string>(LIVE_AREAS);

/**
 * A full picture of every area (the hub's snapshot on connect, or the 30-second check). The first one is the baseline
 * (nothing "moved" when the tab opens). A different nonce means the API restarted: everything may have moved.
 */
export function reconcileSnapshot(serverNonce: string, areas: Readonly<Record<string, number>>): MovedAreas {
  if (nonce !== null && serverNonce !== nonce) {
    nonce = serverNonce;
    known = toMap(areas);
    return ALL;
  }
  const first = known === null;
  nonce = serverNonce;
  const moved = new Set<string>();
  const next = known ?? new Map<string, number>();
  for (const [area, version] of Object.entries(areas)) {
    if (!PUSHABLE.has(area) || typeof version !== "number") continue;
    const before = next.get(area);
    if (!first && (before === undefined || version > before)) moved.add(area);
    if (before === undefined || version > before) next.set(area, version);
  }
  known = next;
  return moved;
}

/** Pushed changes. Versions not newer than what the tab already knows are ignored (duplicates, late arrivals). */
export function reconcileChanges(serverNonce: string, changes: ReadonlyArray<{ area: string; version: number }>): MovedAreas {
  if (nonce !== null && serverNonce !== nonce) {
    // A message from a restarted API before its snapshot reached us: everything may have moved.
    nonce = serverNonce;
    known = new Map(changes.filter((c) => PUSHABLE.has(c.area)).map((c) => [c.area, c.version]));
    return ALL;
  }
  nonce = serverNonce;
  const next = known ?? new Map<string, number>();
  const moved = new Set<string>();
  for (const { area, version } of changes) {
    if (!PUSHABLE.has(area) || typeof version !== "number") continue;
    const before = next.get(area);
    if (before === undefined || version > before) {
      next.set(area, version);
      moved.add(area);
    }
  }
  known = next;
  return moved;
}

/** Reads the nonce out of the overall stamp `GET /data/version` returns ("<nonce>:<area>=<v>;…"). */
export function nonceOfOverall(version: string): string {
  const i = version.indexOf(":");
  return i < 0 ? version : version.slice(0, i);
}

/** Test helper / sign-out: forget everything. */
export function resetLiveVersions(): void {
  nonce = null;
  known = null;
}

function toMap(areas: Readonly<Record<string, number>>): Map<string, number> {
  const map = new Map<string, number>();
  for (const [area, version] of Object.entries(areas)) if (PUSHABLE.has(area) && typeof version === "number") map.set(area, version);
  return map;
}
